import React, { useEffect, useMemo, useState } from 'react';
import { func, shape, arrayOf, string } from 'prop-types';
import { useSelector } from 'react-redux';

import { RoomCard } from '@molecules';
import { Flex, Typography } from '@mixins';
import { getAvailableTimelines, getClosestTime, formatTime } from '@helpers/time';
import { ROOM_TYPES_BY_NAME } from '@constants/common';
import { dayjs } from '@utils';
import { theme } from '@styles';

import Gif from '@assets/gifs/no-rooms.gif';

const RoomsList = ({ onRoomClick, date, timeList, roomTypeSelected, startTime, endTime }) => {
  const [initialStartTime, setInitialStartTime] = useState(-1);
  const [initialEndTime, setInitialEndTime] = useState(-1);

  const {
    rooms: { meetingRooms },
  } = useSelector(store => store.roomStore);

  useEffect(() => {
    if (startTime && endTime) {
      const startIndex = timeList.findIndex(time => time.$d.toString() === startTime.$d.toString());
      const endIndex = timeList.findIndex(time => time.$d.toString() === endTime.$d.toString());
      setInitialStartTime(startIndex);
      setInitialEndTime(endIndex);
    }
  }, [startTime, endTime, timeList]);

  const isEventSpaceSelected = useMemo(() => roomTypeSelected === 'Event Space', [roomTypeSelected]);

  const closestTime = useMemo(() => getClosestTime(), []);

  const timelineScrollToIndex = useMemo(
    () => timeList.findIndex(time => formatTime(time, '24h') === formatTime(closestTime, '24h')),
    [timeList, closestTime],
  );

  const isToday = useMemo(() => date.$d.toString().substring(0, 15) === dayjs().$d.toString().substring(0, 15), [date]);

  const timelinesList = useMemo(
    () =>
      isToday
        ? timeList.filter(timeline => {
            const time = timeline.$d;

            const now = dayjs()
              .minute(dayjs().minute() - 10)
              .second(0)
              .millisecond(0).$d;

            return time >= now;
          })
        : timeList,
    [isToday, timeList],
  );

  const roomsBlock = useMemo(
    () =>
      meetingRooms?.map(
        ({
          id,
          image,
          roomNumber,
          floor,
          workstationCount,
          features,
          creditsPerHour,
          bookings,
          pricePerHour,
          maxBookingMinutes,
          minBookingMinutes,
          ruleMinBookingMinutes,
          defaultBookingMinutes,
          prices,
          roomType,
        }) => {
          const roomAvailableTimelines = getAvailableTimelines(timelinesList, bookings);
          const minBookingHours = minBookingMinutes ? minBookingMinutes / 60 : 0.5;

          return (
            <RoomCard
              roomType={roomType}
              initialStartTime={initialStartTime}
              initialEndTime={initialEndTime}
              creditPrices={prices}
              maxBookingHours={maxBookingMinutes ? maxBookingMinutes / 60 : 24}
              minBookingHours={minBookingHours}
              ruleMinBookingHours={ruleMinBookingMinutes ? ruleMinBookingMinutes / 60 : minBookingHours}
              defaultBookingHours={defaultBookingMinutes ? defaultBookingMinutes / 60 : 1}
              bookings={bookings}
              timelineScrollTo={isToday ? timelineScrollToIndex : 20}
              timeList={timeList}
              onRoomClick={onRoomClick}
              key={id}
              id={id}
              creditsPrice={creditsPerHour}
              currencyPrice={pricePerHour}
              imageUrl={image}
              number={roomNumber}
              floor={floor}
              seats={workstationCount}
              availableTimes={roomAvailableTimelines}
              features={features}
            />
          );
        },
      ),
    [meetingRooms, date, timeList, initialStartTime, initialEndTime],
  );

  return meetingRooms?.length ? (
    roomsBlock
  ) : (
    <Flex width="100%" alignItems="center" flexDirection="column">
      {!isEventSpaceSelected && <Flex as="img" mb={[25, 40, 55]} width={[200, 300]} height={[150, 230]} src={Gif} />}
      <Typography mb={10} variant="rightserif-400" fontSize={[30, 50]}>
        {isEventSpaceSelected ? 'Ugh! Not available' : 'Ugh! All taken'}
      </Typography>
      <Typography variant="diatype-400" fontSize={[15, 21]} color={theme.color.gray[300]}>
        {isEventSpaceSelected
          ? 'Please contact your Community Team for assistance'
          : 'No results found for your search.. try different filters'}
      </Typography>
    </Flex>
  );
};

RoomsList.defaultProps = {
  roomTypeSelected: ROOM_TYPES_BY_NAME['Meeting Room'],
  timeList: [],
  startTime: null,
  endTime: null,
};

RoomsList.propTypes = {
  roomTypeSelected: string,
  timeList: arrayOf(shape({})),
  date: shape({}).isRequired,
  onRoomClick: func.isRequired,
  startTime: shape({}),
  endTime: shape({}),
};

export default RoomsList;
